import { Link, useRouteError } from 'react-router-dom'
import { Compass, ShieldAlert, TriangleAlert } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { Button } from '@/components/ui/Button'
import styles from './ErrorPages.module.css'

interface ShellProps {
  icon: React.ReactNode
  code?: string
  title: string
  description: string
  children?: React.ReactNode
}

function ErrorShell({ icon, code, title, description, children }: ShellProps) {
  return (
    <div className={styles.page} role="alert">
      <div className={styles.panel}>
        <span className={styles.icon}>{icon}</span>
        {code ? <p className={styles.code}>{code}</p> : null}
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.description}>{description}</p>
        <div className={styles.actions}>{children}</div>
      </div>
    </div>
  )
}

export function NotFoundPage() {
  return (
    <ErrorShell
      icon={<Compass aria-hidden="true" />}
      code="404"
      title="That page does not exist"
      description="The link may be out of date, or the record it pointed to has been removed."
    >
      <Link to="/" className={styles.primaryLink}>
        Back to the overview
      </Link>
    </ErrorShell>
  )
}

export function ForbiddenPage() {
  return (
    <ErrorShell
      icon={<ShieldAlert aria-hidden="true" />}
      code="403"
      title="You do not have access"
      description="This console is limited to accounts with the administrator role. If you believe this is a mistake, ask another administrator to check your account."
    >
      <Link to="/login" className={styles.primaryLink}>
        Sign in with another account
      </Link>
    </ErrorShell>
  )
}

/**
 * Catches anything a route throws — including a render-time crash — so a single
 * broken screen never takes down the whole console.
 */
export function RouteErrorBoundary() {
  const error = useRouteError()

  const description =
    error instanceof ApiError
      ? error.message
      : error instanceof Error && error.message
        ? error.message
        : 'An unexpected error occurred while loading this screen.'

  return (
    <ErrorShell
      icon={<TriangleAlert aria-hidden="true" />}
      title="This screen could not be loaded"
      description={description}
    >
      <Button variant="primary" onClick={() => window.location.reload()}>
        Reload the page
      </Button>
      <Link to="/" className={styles.secondaryLink}>
        Back to the overview
      </Link>
    </ErrorShell>
  )
}
